import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Film, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

/**
 * RendyPresetsPreviewModal — "Preview Rendy presets" button shown above the
 * image gallery in both the agent and master admin property edit modals.
 * Browsing/reference tool for the presets, staging styles, staging
 * animations, and camera movements Rendy currently offers. When `editable`
 * (master admin only), each preset also gets an enable/disable switch —
 * disabled presets are hidden from the per-image preset picker
 * (`RendyImageOptionsDialog`) that agents use.
 */
export default function RendyPresetsPreviewModal({ editable = false }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  useEffect(() => {
    if (!open || options) return;
    setLoading(true);
    axios
      .get(`${API}/rendy/options`, { withCredentials: true })
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions({ presets: [], staging_styles: [], staging_animations: [], camera_movements: [] }))
      .finally(() => setLoading(false));
  }, [open, options]);

  const handleToggle = async (presetKey, enabled) => {
    setTogglingKey(presetKey);
    try {
      await axios.put(
        `${API}/admin/integrations/rendy-presets/${presetKey}/toggle`,
        { enabled },
        { withCredentials: true }
      );
      setOptions((prev) => ({
        ...prev,
        presets: prev.presets.map((p) => (p.key === presetKey ? { ...p, enabled } : p)),
      }));
      toast.success(enabled ? "Preset enabled" : "Preset disabled — hidden from agents' preset picker");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update preset");
    } finally {
      setTogglingKey(null);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="rounded-none text-xs"
        data-testid="preview-rendy-presets-btn"
      >
        <Film className="mr-1 h-3 w-3" />
        Preview Rendy Presets
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] sm:max-w-3xl rounded-none max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="rendy-presets-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" /> Rendy Video Presets
            </DialogTitle>
            <DialogDescription>
              Browse every effect, staging style, staging animation, and camera movement currently available from Rendy.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading presets…
            </div>
          ) : options?.configured === false ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="rendy-presets-not-configured">
              Rendy isn't configured yet — add an API key under Admin → Integrations → Rendy Testing.
            </p>
          ) : (
            <Tabs defaultValue="presets">
              <TabsList>
                <TabsTrigger value="presets" data-testid="rendy-presets-tab-presets">Presets ({options?.presets?.length || 0})</TabsTrigger>
                <TabsTrigger value="styles" data-testid="rendy-presets-tab-styles">Staging Styles ({options?.staging_styles?.length || 0})</TabsTrigger>
                <TabsTrigger value="animations" data-testid="rendy-presets-tab-animations">Staging Animations ({options?.staging_animations?.length || 0})</TabsTrigger>
                <TabsTrigger value="movements" data-testid="rendy-presets-tab-movements">Camera Movements ({options?.camera_movements?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="presets" className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(options?.presets || []).map((p) => (
                    <div
                      key={p.key}
                      className={`border border-border p-2 space-y-1 ${p.enabled === false ? "opacity-50" : ""}`}
                      data-testid={`rendy-preset-card-${p.key}`}
                    >
                      {p.sampleVideoUrl ? (
                        <video src={p.sampleVideoUrl} muted loop playsInline controls className="w-full aspect-video bg-black object-cover" />
                      ) : p.iconUrl ? (
                        <img src={p.iconUrl} alt={p.name} className="w-full aspect-video object-cover bg-muted" />
                      ) : (
                        <div className="w-full aspect-video bg-muted flex items-center justify-center text-muted-foreground">
                          <Film className="h-6 w-6" />
                        </div>
                      )}
                      <p className="text-xs font-semibold truncate">{p.name}</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {p.staging && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">Staging</span>}
                        {p.enabled === false && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full">Disabled</span>}
                      </div>
                      {p.description && <p className="text-[11px] text-muted-foreground line-clamp-2">{p.description}</p>}
                      {editable && (
                        <div className="flex items-center justify-between pt-1 border-t border-border/60 mt-1">
                          <span className="text-[11px] text-muted-foreground">
                            {p.enabled === false ? "Disabled" : "Enabled"}
                          </span>
                          {togglingKey === p.key ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                          ) : (
                            <Switch
                              checked={p.enabled !== false}
                              onCheckedChange={(checked) => handleToggle(p.key, checked)}
                              data-testid={`rendy-preset-toggle-${p.key}`}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="styles" className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(options?.staging_styles || []).map((s) => (
                    <div key={s.id} className="border border-border p-3 text-sm text-center" data-testid={`rendy-style-card-${s.id}`}>
                      {s.name}
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="animations" className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(options?.staging_animations || []).map((a) => (
                    <div key={a.id} className="border border-border p-2 space-y-1" data-testid={`rendy-animation-card-${a.id}`}>
                      {a.previewUrl ? (
                        <video src={a.previewUrl} muted loop playsInline controls className="w-full aspect-video bg-black object-cover" />
                      ) : (
                        <div className="w-full aspect-video bg-muted flex items-center justify-center text-muted-foreground">
                          <Film className="h-6 w-6" />
                        </div>
                      )}
                      <p className="text-xs font-semibold truncate">{a.name}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="movements" className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(options?.camera_movements || []).map((m) => (
                    <div key={m.key} className="border border-border p-3 text-sm" data-testid={`rendy-movement-card-${m.key}`}>
                      <p className="font-semibold text-xs">{m.key}</p>
                      <p className="text-[11px] text-muted-foreground">{m.movement} · {m.direction}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
